import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { t } from "../lib/i18n";
import { CText } from "./CText";

const PHONES = [
  { display: "+595 981 858 770", raw: "595981858770" },
  { display: "+595 971 339 332", raw: "595971339332" },
];
const ADDRESS = "Charles de Gaulle 1565 — Asunción";
const IG_HANDLE = "impeclandpy";
const FB_HANDLE = "impeclandpy";

const WhatsAppIcon = ({ className = "", size = 14 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.003 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.255.59 4.453 1.71 6.39L3.2 28.8l6.553-1.717a12.74 12.74 0 0 0 6.247 1.617h.005c7.06 0 12.795-5.73 12.795-12.8 0-3.42-1.331-6.633-3.749-9.05A12.713 12.713 0 0 0 16.003 3.2zm0 23.36h-.005a10.55 10.55 0 0 1-5.382-1.473l-.385-.23-3.886 1.018 1.038-3.79-.252-.39A10.547 10.547 0 0 1 5.45 16c0-5.83 4.74-10.57 10.557-10.57 2.821 0 5.473 1.099 7.467 3.094a10.487 10.487 0 0 1 3.087 7.482c0 5.83-4.741 10.555-10.558 10.555zm5.778-7.91c-.317-.158-1.876-.926-2.166-1.032-.29-.106-.502-.158-.713.158-.21.317-.819 1.031-1.004 1.243-.185.211-.37.238-.687.08-.317-.159-1.337-.493-2.547-1.572-.941-.84-1.577-1.876-1.762-2.193-.185-.317-.02-.488.139-.646.143-.143.317-.37.476-.555.159-.185.211-.317.317-.529.106-.211.053-.396-.026-.555-.08-.158-.713-1.717-.977-2.351-.257-.617-.518-.534-.713-.544l-.608-.011c-.211 0-.555.08-.846.396-.291.317-1.111 1.085-1.111 2.644 0 1.559 1.137 3.066 1.296 3.278.158.211 2.234 3.41 5.413 4.78.756.327 1.346.522 1.806.668.759.241 1.45.207 1.997.126.61-.091 1.876-.766 2.14-1.506.265-.74.265-1.374.185-1.506-.08-.132-.291-.211-.608-.37z"/>
  </svg>
);

export const Footer = () => {
  const { lang } = useLanguage();
  const { get } = useContent();
  const email = get("contact", "email", "impeclandpy@gmail.com");

  return (
    <footer className="border-t border-[#C5A059]/15 mt-32" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand + description */}
        <div className="md:col-span-5">
          <div className="font-serif-display text-3xl tracking-wider mb-2 notranslate whitespace-nowrap" translate="no">
            <span className="text-white">IMPEC</span><span className="text-[#C5A059] -ml-[0.1em]">LAND</span>
          </div>
          <CText section="footer" field="tagline" as="div" className="text-[10px] tracking-[0.3em] text-[#C5A059] uppercase mb-6" />
          <CText section="footer" field="description" as="p" className="text-[#A3A9B2] text-sm max-w-md leading-relaxed mb-8" />

          {/* Social */}
          <div className="flex gap-3">
            <a
              href={`https://instagram.com/${IG_HANDLE}`}
              target="_blank" rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex items-center gap-2 border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059]/10 px-3 py-2 text-xs text-[#A3A9B2] hover:text-white transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={14} className="text-[#C5A059]" /> @{IG_HANDLE}
            </a>
            <a
              href={`https://facebook.com/${FB_HANDLE}`}
              target="_blank" rel="noopener noreferrer"
              aria-label="Facebook"
              className="flex items-center gap-2 border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059]/10 px-3 py-2 text-xs text-[#A3A9B2] hover:text-white transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook size={14} className="text-[#C5A059]" /> @{FB_HANDLE}
            </a>
          </div>
        </div>

        {/* Contacto */}
        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-4">Contacto</div>
          <div className="space-y-3 text-sm text-[#A3A9B2]">
            {PHONES.map((p) => (
              <a
                key={p.raw}
                href={`https://wa.me/${p.raw}?text=${encodeURIComponent("Hola, quisiera más información sobre la propiedad ubicada en zona...")}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-white transition-colors group"
                data-testid={`footer-whatsapp-${p.raw}`}
              >
                <WhatsAppIcon size={16} className="text-[#C5A059] group-hover:text-[#E5C07B] transition-colors shrink-0" />
                <span className="font-sans-body tracking-wide">{p.display}</span>
              </a>
            ))}
            <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-white transition-colors break-all pt-1">
              <Mail size={14} className="text-[#C5A059] shrink-0" /> {email}
            </a>
            <div className="flex items-start gap-3 pt-1">
              <MapPin size={14} className="text-[#C5A059] mt-0.5 shrink-0" />
              <span>{ADDRESS}</span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-4">Menu</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/" className="link-copper text-[#A3A9B2] hover:text-white">{t(lang, "nav_home")}</Link>
            <Link to="/gallery" className="link-copper text-[#A3A9B2] hover:text-white">{t(lang, "nav_gallery")}</Link>
            <Link to="/about" className="link-copper text-[#A3A9B2] hover:text-white">{t(lang, "nav_about")}</Link>
            <Link to="/contact" className="link-copper text-[#A3A9B2] hover:text-white">{t(lang, "nav_contact")}</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-[#C5A059]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A3A9B2]">
          <div>© {new Date().getFullYear()} ImpecLand. {t(lang, "footer_rights")}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059]/70">Asunción · Paraguay</div>
        </div>
      </div>
    </footer>
  );
};
